/* eslint-disable max-lines */

import type {SharedData} from '@gaia/types';
import {syncGetTrackLines} from '@gaia/utils/gaia/getTrackLines';
import snapTrackPoint from '@gaia/utils/gaia/snapTrackPoint';
import concatMapboxStyles from '@gaia/utils/mapbox/concatMapboxStyles';
import type {GetSyncPromise} from '@gaia/utils/react/useGetSyncPromise';
import compact from 'lodash/compact';
import lodashFilter from 'lodash/filter';
import flatten from 'lodash/flatten';
import fromPairs from 'lodash/fromPairs';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';

import {activeLineLayers, blurOpacity} from './constants';
import {trackFixedLayerIds} from './fixedLayerIds';

interface GetTracksMapboxStyleOptions {
    showTracks: boolean;
    showRoutes: boolean;
    tracks: {[id: string]: Track};
    project: MapProject;
    interaction: InteractionState;
    getSyncPromise: GetSyncPromise;
    sharedData: SharedData;
    isPrinting: boolean;
    isAuthenticated: boolean;
    selections: SelectionState[];
    userSource: mapboxgl.VectorSource | undefined;
    visibilities: Record<string, boolean>;
    showArchived: boolean;
    activeFolderIds: string[] | undefined;
}

type LocalFixedLayerIds = (typeof trackFixedLayerIds)['localUser'];
type LocalSharedFixedLayerIds = (typeof trackFixedLayerIds)['localShared'];
type RemoteFixedLayerIds = (typeof trackFixedLayerIds)['remoteUserTracks'];
type EditingFixedLayerIds = (typeof trackFixedLayerIds)['editing'];

export function getTracksMapboxStyle(options: GetTracksMapboxStyleOptions) {
    return {
        metadata: {
            id: 'tracks',
        },
        ...concatMapboxStyles(
            compact([
                getRemoteUserStyle(trackFixedLayerIds.remoteUserTracks, options),
                getLocalStyle(
                    'localUser',
                    pickBy(
                        options.tracks,
                        (track) =>
                            !isSharedData(options.sharedData, track) &&
                            !track.properties.isPublicTrack &&
                            (!options.showArchived
                                ? !track.properties.deleted && !track.properties.archived
                                : true)
                    ),
                    trackFixedLayerIds.localUser,
                    options
                ),
                getRemoteSharedStyle(trackFixedLayerIds.remoteShared, options),
                getLocalStyle(
                    'localShared',
                    pickBy(
                        options.tracks,
                        (track) =>
                            isSharedData(options.sharedData, track) &&
                            (!options.showArchived
                                ? !track.properties.deleted && !track.properties.archived
                                : true)
                    ),
                    trackFixedLayerIds.localShared,
                    options
                ),
                options.selections.length &&
                    getLocalStyle(
                        'localPublic',
                        pickBy(
                            options.tracks,
                            (track) =>
                                track.properties.isPublicTrack &&
                                options.selections
                                    .map(
                                        (selection) =>
                                            selection.type === 'track' && selection.localId
                                    )
                                    .includes(track.properties.localId)
                        ),
                        trackFixedLayerIds.localShared,
                        options
                    ),
                getEditingStyle(trackFixedLayerIds.editing, options),
            ])
        ),
    };
}

function isSharedData(sharedData: SharedData, track: Track) {
    if (!sharedData) {
        return false;
    } else if (sharedData.type === 'track') {
        return sharedData.id === track.properties.localId;
    } else if (sharedData.type === 'folder') {
        return track.properties.folder === sharedData.id;
    } else {
        return false;
    }
}

function getEditingStyle(
    layerIds: EditingFixedLayerIds,
    {
        interaction,
        project,
        getSyncPromise,
        selections,
        isPrinting,
        visibilities,
        activeFolderIds,
    }: GetTracksMapboxStyleOptions
): mapboxgl.Style | undefined {
    if (!project || interaction.type !== 'editingRoute') return undefined;

    const line = compact(
        syncGetTrackLines({
            track: interaction.track,
            project,
            interaction,
            getSyncPromise,
            includeAppendPreviewLine: true,
        })[0]
    );

    const points: TrackPoint[] =
        interaction.track.isGeometryLoaded && interaction.track.properties.track_type === 'route'
            ? map(interaction.track.points, (point) => ({
                  ...point,
                  geometry: getSyncPromise(
                      snapTrackPoint({
                          point,
                          track: interaction.track,
                          project,
                          interaction,
                      }),
                      point.geometry
                  )[0],
              })).filter((point) => !point.properties.archived && !point.properties.deleted)
            : [];

    if (line.length === 0 && points.length === 0) return;

    // Tracks and imported routes have no points
    const sortedPointOrders =
        points.length > 0
            ? points
                  .sort((a, b) => a.properties.order - b.properties.order)
                  .map((p) => p.properties.order)
            : [0, 1];
    const lastPointOrder = sortedPointOrders[sortedPointOrders.length - 1];

    const firstPointOrder = sortedPointOrders[0];

    const previewFilter = ['all', ['==', ['get', 'type'], 'preview'], ['get', 'isGeometryLoaded']];

    const normalFilter = ['all', ['==', ['get', 'type'], 'normal'], ['get', 'isGeometryLoaded']];

    const firstOrLastPointFilter = [
        'any',
        ['==', ['get', 'order'], firstPointOrder],
        ['==', ['get', 'order'], lastPointOrder],
    ];

    const hoverPointId =
        (interaction.subtype === 'hoveringPoint' || interaction.subtype === 'draggingPoint') &&
        interaction.pointLocalId;

    const showPointOrder = DEPLOYMENT !== 'production';

    return {
        version: 8,
        metadata: {
            id: 'editing',
        },
        sources: {
            lines: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: line,
                },
            },
            points: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: points,
                },
            },
        },
        layers: compact([
            getCasingLayer({
                id: layerIds.normalCasing,
                interaction,
                selections,
                isPrinting,
                visibilities,
                filter: normalFilter,
                activeFolderIds,
            }),

            getLineLayer({
                id: layerIds.normal,
                interaction,
                filter: normalFilter,
                paint: {'line-color': interaction.track.properties.color},
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getCasingLayer({
                id: layerIds.previewCasing,
                interaction,
                selections,
                isPrinting,
                visibilities,
                filter: previewFilter,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.preview,
                interaction,
                filter: previewFilter,
                paint: {
                    'line-dasharray': [1, 1.25],
                    'line-color': interaction.track.properties.color,
                },
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),

            getDirectionArrowLayer({
                id: layerIds.directionArrow,
                interaction,
                filter: normalFilter,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.loading,
                interaction,
                filter: ['!', ['get', 'isGeometryLoaded']],
                paint: {
                    'line-dasharray': [0, 1.25],
                    'line-color': interaction.track.properties.color,
                },
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            {
                id: layerIds.point,
                type: 'symbol',
                source: 'points',
                paint: {
                    'icon-opacity': 1,
                    ...(showPointOrder && {
                        'text-halo-color': '#fff',
                        'text-halo-width': 4,
                    }),
                },
                layout: {
                    'icon-image': getControlPointImageExpression(
                        points.length,
                        interaction.track.properties.color || 'rebeccapurple',
                        String(hoverPointId),
                        lastPointOrder
                    ),
                    'icon-size': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        8,
                        [
                            'case',
                            ['==', ['get', 'localId'], hoverPointId],
                            0.6, // hover point
                            firstOrLastPointFilter,
                            0.35, // first and last points
                            0.25, // middle points
                        ],
                        12,
                        [
                            'case',
                            ['==', ['get', 'localId'], hoverPointId],
                            0.6,
                            firstOrLastPointFilter,
                            0.4,
                            0.3,
                        ],
                        14,
                        [
                            'case',
                            ['==', ['get', 'localId'], hoverPointId],
                            0.7,
                            firstOrLastPointFilter,
                            0.6,
                            0.4,
                        ],
                    ],
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': showPointOrder,
                    'symbol-sort-key': 0,
                    ...(showPointOrder && {
                        'text-field': ['get', 'order'],
                        'text-ignore-placement': false,
                        'text-allow-overlap': false,
                        'text-size': 12,
                        'text-anchor': 'top',
                        'text-font': ['Regular'],
                        'text-offset': [0, 1],
                    }),
                },
            },
        ]),
    };
}

function getControlPointImageExpression(
    numPoints: number,
    color: string,
    hoveringPointId: string,
    lastPointOrder: number
): mapboxgl.Expression {
    return [
        'concat',
        'control-point.',
        [
            'case',
            ['==', ['get', 'order'], 0],
            'start',
            ['==', ['get', 'order'], lastPointOrder],
            'end',
            'middle',
        ],
        '.',
        color,
        '.',
        ['case', ['==', ['get', 'localId'], hoveringPointId], 'selected', 'idle'],
    ];
}

function getLocalStyle(
    id: string,
    tracks: Record<string, Track>,
    layerIds: LocalFixedLayerIds | LocalSharedFixedLayerIds,
    {
        interaction,
        project,
        getSyncPromise,
        selections,
        sharedData,
        isPrinting,
        visibilities,
        showArchived,
        showTracks,
        showRoutes,
        activeFolderIds,
    }: GetTracksMapboxStyleOptions
): mapboxgl.Style | undefined {
    if (!project) return undefined;

    const editingRouteLocalId =
        interaction.type === 'editingRoute' ? interaction.track.properties.localId : undefined;

    const filteredTracks = lodashFilter(
        tracks,
        (track: Track) =>
            track.properties.localId !== editingRouteLocalId && !track.properties.archived
    );

    const lines = flatten(
        compact(
            map(
                filteredTracks,
                (track) =>
                    syncGetTrackLines({
                        track,
                        project,
                        interaction,
                        getSyncPromise,
                        includeAppendPreviewLine: true,
                    })[0]
            )
        )
    );

    if (lines.length === 0) return;

    const lineColorExpression: mapboxgl.Expression = [
        'get',
        ['coalesce', ['get', 'localId'], ['get', 'id']],
        ['literal', pickBy(mapValues(tracks, (track) => track.properties.color))],
    ];

    const filter = getLocalFilter(
        tracks,
        visibilities,
        interaction,
        selections,
        sharedData,
        showArchived,
        showTracks,
        showRoutes,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id,
        },
        sources: {
            lines: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: lines,
                },
            },
        },
        layers: compact([
            getCasingLayer({
                id: layerIds.casing,
                interaction,
                selections,
                isPrinting,
                visibilities,
                filter,
                activeFolderIds,
            }),

            getLineLayer({
                id: layerIds.normal,
                interaction,
                filter: [
                    'all',
                    filter,
                    ['==', ['get', 'type'], 'normal'],
                    ['get', 'isGeometryLoaded'],
                ],
                paint: {'line-color': lineColorExpression},
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.preview,
                interaction,
                filter: [
                    'all',
                    filter,
                    ['==', ['get', 'type'], 'preview'],
                    ['get', 'isGeometryLoaded'],
                ],
                paint: {
                    'line-dasharray': [1, 1.25],
                    'line-color': lineColorExpression,
                },
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.loading,
                interaction,
                filter: ['all', filter, ['!', ['get', 'isGeometryLoaded']]],
                paint: {
                    'line-dasharray': [0, 1.25],
                    'line-color': lineColorExpression,
                },
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            ...getSelectionLayer(
                selections,
                lineColorExpression,
                visibilities,
                editingRouteLocalId
            ),
            getDirectionArrowLayer({
                id: layerIds.directionArrow,
                interaction,
                filter: [
                    'all',
                    filter,
                    ['==', ['get', 'type'], 'normal'],
                    ['get', 'isGeometryLoaded'],
                ],
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ]),
    };
}

function getRemoteSharedStyle(
    layerIds: RemoteFixedLayerIds,
    {
        tracks,
        interaction,
        sharedData,
        selections,
        isPrinting,
        showTracks,
        showRoutes,
        visibilities,
        showArchived,
        activeFolderIds,
    }: GetTracksMapboxStyleOptions
): mapboxgl.Style | undefined {
    const source = getRemoteSharedSource(sharedData);
    if (!source) return;

    const filter = [
        'all',
        getRemoteFilter(
            tracks,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showTracks,
            showRoutes,
            activeFolderIds
        ),
    ];

    return {
        version: 8,
        metadata: {
            id: 'remoteShared',
        },
        sources: {source},
        layers: [
            getCasingLayer({
                id: layerIds.casing,
                interaction,
                source: 'source',
                ...(source.type === 'vector' && {'source-layer': 'tracks'}),
                filter,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                source: 'source',
                ...(source.type === 'vector' && {'source-layer': 'tracks'}),
                filter,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getDirectionArrowLayer({
                source: 'source',
                ...(source.type === 'vector' && {'source-layer': 'tracks'}),
                filter,
                id: layerIds.directionArrow,
                interaction,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ],
    };
}

function getRemoteSharedSource(sharedData: SharedData): mapboxgl.AnySourceData | undefined {
    if (sharedData && sharedData.type === 'track') {
        return {
            type: 'geojson',
            data: `${window.location.origin}/api/objects/track/${sharedData.id}/?pubLink=${sharedData.pubLink}`,
        };
    } else if (sharedData && sharedData.type === 'folder') {
        return {
            type: 'vector',
            tiles: [
                `${window.location.origin}/api/tiles/folders/${sharedData.id}/{z}/{x}/{y}.mvt?pubLink=${sharedData.pubLink}`,
            ],
        };
    }
}

function getRemoteUserStyle(
    layerIds: RemoteFixedLayerIds,
    {
        tracks,
        interaction,
        project,
        sharedData,
        isAuthenticated,
        selections,
        isPrinting,
        visibilities,
        showArchived,
        userSource,
        showTracks,
        showRoutes,
        activeFolderIds,
    }: GetTracksMapboxStyleOptions
): mapboxgl.Style | undefined {
    if (!project || !isAuthenticated || !userSource) return undefined;

    const filter = [
        'all',
        getRemoteFilter(
            tracks,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showTracks,
            showRoutes,
            activeFolderIds
        ),
        sharedData && sharedData.type === 'track'
            ? ['!=', ['get', 'id'], sharedData.id]
            : ['literal', true],
    ];

    return {
        version: 8,
        metadata: {
            id: `remoteUserTracks`,
        },
        sources: {
            source: userSource,
        },
        layers: [
            getCasingLayer({
                id: layerIds.casing,
                interaction,
                source: 'source',
                'source-layer': 'tracks',
                filter,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                source: 'source',
                'source-layer': 'tracks',
                filter,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getDirectionArrowLayer({
                source: 'source',
                'source-layer': 'tracks',
                filter,
                id: layerIds.directionArrow,
                interaction,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ],
    };
}

interface GetCasingLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: InteractionState;
    id: string;
    selections: SelectionState[];
    isPrinting: boolean;
    visibilities: Record<string, boolean>;
    activeFolderIds: string[] | undefined;
}

function getCasingLayer({
    interaction,
    paint,
    selections,
    isPrinting,
    visibilities,
    activeFolderIds,
    ...layer
}: GetCasingLayerOptions): mapboxgl.LineLayer {
    return {
        type: 'line',
        source: 'lines',
        ...omit(layer, 'type'),
        paint: {
            'line-color': '#fff',
            'line-width': getFocusExpression(interaction, selections, 6, 3, 4, activeFolderIds),
            'line-opacity': getFocusExpression(
                interaction,
                selections,
                getVisibilityExpression(visibilities, 0.7, 0.3),
                isPrinting ? 0 : blurOpacity,
                0.9,
                activeFolderIds
            ),
            ...paint,
        },
    };
}

interface GetLineLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: InteractionState;
    id: string;
    selections: SelectionState[];
    isPrinting: boolean;
    visibilities: Record<string, boolean>;
    activeFolderIds: string[] | undefined;
}

function getLineLayer({
    paint,
    interaction,
    layout,
    selections,
    isPrinting,
    visibilities,
    activeFolderIds,
    ...layer
}: GetLineLayerOptions): mapboxgl.LineLayer {
    return {
        type: 'line',
        source: 'lines',
        ...omit(layer, 'type'),
        paint: {
            'line-color': ['get', 'color'],
            // focused and normal are the same width because we now use getSelectionLayer
            // instead of making a thicker colored line
            'line-width': getFocusExpression(interaction, selections, 3, 2, 3, activeFolderIds),
            'line-opacity': getFocusExpression(
                interaction,
                selections,
                getVisibilityExpression(visibilities, 0.9, 0.4),
                isPrinting ? 0 : blurOpacity,
                1,
                activeFolderIds
            ),
            ...paint,
        },
        layout: {
            'line-cap': 'round',
            ...layout,
        },
    };
}

function getDirectionArrowLayer({
    interaction,
    filter,
    selections,
    visibilities,
    activeFolderIds,
    ...layer
}: GetLineLayerOptions): mapboxgl.SymbolLayer {
    return {
        type: 'symbol',
        source: 'lines',
        ...omit(layer, 'type'),
        layout: {
            'icon-image': 'trailDirectionIcon',
            'icon-size': 0.6,
            'icon-allow-overlap': false,
            'symbol-placement': 'line',
            'symbol-spacing': 80,
            'icon-keep-upright': false,
            'icon-ignore-placement': false,
        },
        paint: {
            'icon-opacity': getVisibilityExpression(visibilities, 0.65, 0.4),
        },
        filter: [
            'all',
            getFocusExpression(interaction, selections, true, false, false, activeFolderIds),
            filter,
        ],
    };
}

function getSelectionLayer(
    selections: SelectionState[],
    lineColorExpression: mapboxgl.Expression,
    visibilities: Record<string, boolean>,
    editingRouteLocalId?: string
): mapboxgl.LineLayer[] {
    let highlightId: string | null = null;
    if (editingRouteLocalId) {
        highlightId = editingRouteLocalId;
    } else if (selections.length > 0 && selections[0].type === 'track') {
        highlightId = selections[0].localId;
    }

    const {focusedShadow, focused} = trackFixedLayerIds.localUser;
    const [shadowLayer, lineLayer] = activeLineLayers(
        focused,
        focusedShadow,
        'lines',
        lineColorExpression
    );

    const opacityExpression = compact(
        selections.map((selection) => {
            if (selection.type === 'track' && visibilities[selection.localId]) {
                return [
                    '==',
                    ['coalesce', ['get', 'localId'], ['get', 'id'], ''],
                    selection.localId,
                ];
            } else {
                return undefined;
            }
        })
    );

    const opacity: mapboxgl.Expression = [
        'case',
        ['any', ...opacityExpression],
        1,
        blurOpacity + 0.2,
    ];

    return [
        {
            ...shadowLayer,
            filter: ['==', ['coalesce', ['get', 'localId'], ['get', 'id']], highlightId],
            paint: {
                ...shadowLayer.paint,
                'line-opacity': opacity,
            },
        },
        {
            ...lineLayer,
            filter: ['==', ['coalesce', ['get', 'localId'], ['get', 'id']], highlightId],
            paint: {
                ...lineLayer.paint,
                'line-opacity': opacity,
            },
        },
    ];
}

// eslint-disable-next-line max-params
function getRemoteFilter(
    tracks: Record<string, Track>,
    visibilities: Record<string, boolean>,
    interaction: InteractionState,
    selections: SelectionState[],
    sharedData: SharedData,
    showArchived: boolean,
    showTracks: boolean,
    showRoutes: boolean,
    activeFolderIds: string[] | undefined
) {
    return compact([
        'all',
        [
            '!',
            [
                'has',
                ['get', 'id'],
                [
                    'literal',
                    fromPairs(
                        map(
                            pickBy(
                                tracks,
                                (track) => track.isGeometryLoaded && !track.properties.isPublicTrack
                            ),
                            (track) => [track.properties.id, true]
                        )
                    ),
                ],
            ],
        ],
        getLocalFilter(
            tracks,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showTracks,
            showRoutes,
            activeFolderIds
        ),
    ]);
}

// eslint-disable-next-line max-params
function getLocalFilter(
    tracks: Record<string, Track>,
    visibilities: Record<string, boolean>,
    interaction: InteractionState,
    selections: SelectionState[],
    sharedData: SharedData,
    showArchived: boolean,
    showTracks: boolean,
    showRoutes: boolean,
    activeFolderIds: string[] | undefined
) {
    // Remote sources only have a deleted property, not an archived property
    const archivedFilter = !showArchived
        ? ['!=', ['coalesce', ['get', 'archived'], ['get', 'deleted']], true]
        : undefined;

    const sharedTrackId = sharedData?.type === 'track' && sharedData.id;

    const selectedTrackIds = compact(
        [sharedTrackId].concat(
            selections.map((selection) => selection.type === 'track' && selection.localId)
        )
    );

    const selectedLocalRouteIds = Object.values(
        pickBy(tracks, (track) => track.properties.track_type === 'route')
    )
        .map((route) => route.properties.localId)
        .filter((localId) => selectedTrackIds.includes(localId));

    const selectedLocalTrackIds = Object.values(
        pickBy(tracks, (track) => track.properties.track_type !== 'route')
    )
        .map((track) => track.properties.localId)
        .filter((localId) => selectedTrackIds.includes(localId));

    let filter: mapboxgl.Expression | undefined;

    // Don't filter if showing all
    if (showTracks && showRoutes) {
        filter = undefined;
    } else if (showTracks) {
        // Show only tracks and selected routes
        filter = [
            'case',
            ['!=', ['get', 'track_type'], 'route'],
            true,
            [
                'has',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                ['literal', fromPairs(selectedLocalRouteIds.map((id) => [id, true]))],
            ],
        ];
    } else if (showRoutes) {
        // Show only routes and selected tracks
        filter = [
            'case',
            ['==', ['get', 'track_type'], 'route'],
            true,
            [
                'has',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                ['literal', fromPairs(selectedLocalTrackIds.map((id) => [id, true]))],
            ],
        ];
    } else if (!(sharedData?.type === 'folder')) {
        // Show only selected items
        return [
            'has',
            ['coalesce', ['get', 'localId'], ['get', 'id']],
            ['literal', fromPairs(selectedTrackIds.map((id) => [id, true]))],
        ];
    }

    return compact([
        'all',
        archivedFilter,
        getFocusExpression(
            interaction,
            selections,
            getVisibilityExpression(visibilities, true, false),
            getVisibilityExpression(visibilities, true, false),
            getVisibilityExpression(visibilities, true, false),
            activeFolderIds
        ),
        filter,
    ]);
}

// eslint-disable-next-line max-params
function getFocusExpression<T>(
    interaction: InteractionState,
    selections: SelectionState[],
    focused: T | mapboxgl.Expression,
    blurred: T | mapboxgl.Expression,
    normal: T | mapboxgl.Expression,
    activeFolderIds: string[] | undefined
): T | mapboxgl.Expression {
    if (interaction.type === 'editingRoute') {
        return [
            'case',
            [
                '==',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                interaction.track.properties.localId,
            ],
            focused,
            blurred,
        ];
    } else if (interaction.type === 'editingWaypoint' || interaction.type === 'editingArea') {
        // Always blur when a different data type is being edited
        return blurred;
    } else if (selections.length) {
        const inner = compact(
            selections.map((selection) => {
                if (selection.type === 'track') {
                    return [
                        '==',
                        ['coalesce', ['get', 'localId'], ['get', 'id']],
                        selection.localId,
                    ];
                } else {
                    return undefined;
                }
            })
        );
        if (!inner.length) return blurred;
        else return ['case', ['any', ...inner], focused, blurred];
    } else if (activeFolderIds) {
        // Remote items will have a folder property, but items that have been edited locally
        // will have a parent_folder_id that is more current than the original folder from the server.
        return [
            'case',
            [
                'in',
                [
                    'case',
                    ['has', 'parent_folder_id'],
                    ['get', 'parent_folder_id'],
                    ['get', 'folder'],
                ],
                ['literal', activeFolderIds],
            ],
            focused,
            blurred,
        ];
    } else if (interaction.type !== 'normal') {
        return blurred;
    } else {
        return normal;
    }
}

function getVisibilityExpression<T>(
    visibilities: Record<string, boolean>,
    visible: T | mapboxgl.Expression,
    hidden: T | mapboxgl.Expression
): T | mapboxgl.Expression {
    return [
        'case',
        [
            'all',
            ['has', 'id'],
            [
                'has',
                ['get', 'id'],
                ['literal', pickBy(visibilities, (visibility) => visibility === false)],
            ],
        ],
        hidden,
        visible,
    ];
}
